import { useState } from 'react';

import useRequestError from 'components/hooks/useRequestError';

import {
  isAccountPermissionInferable,
  isAnyAssignablePermissionSelected,
  permissions,
} from 'components/helpers/users';

export const defaultCurrentUser = {
  firstName: '',
  lastName: '',
  email: '',
  position: '',
  isSelected: false,
  accessType: 'regular',
  accessControls: {},
};

const domainUserAsForm = (domainUser, assignablePermissionsPerDivision) => {
  if (!domainUser) {
    return null;
  }

  const { accessType, email, firstName, lastName, position } =
    domainUser.data.attributes;

  return {
    ...defaultCurrentUser,
    isSelected: true,
    firstName,
    lastName,
    email,
    position,
    accessType,
    accessControls: accessControlsFromDomainUser(
      domainUser,
      assignablePermissionsPerDivision,
    ),
  };
};

export default function useUserForm({
  assignablePermissionsPerDivision,
  domainUser,
}) {
  const seedUser = domainUserAsForm(
    domainUser,
    assignablePermissionsPerDivision,
  );

  const [currentUser, setCurrentUser] = useState(
    seedUser || defaultCurrentUser,
  );

  const [
    requestError,
    _submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const resetUserForm = () => setCurrentUser(seedUser || defaultCurrentUser);

  const resetAccessControls = () => {
    const { accessControls, accessType } = seedUser || defaultCurrentUser;

    setCurrentUser((prevState) => ({
      ...prevState,
      accessControls,
      accessType,
    }));
  };

  return {
    currentUser,
    setCurrentUser,
    resetUserForm,
    resetAccessControls,
    requestError,
    handleRequestError,
    resetRequestError,
    removeErrorStyling,
  };
}

export function accessControlsFromDomainUser(
  domainUser,
  assignablePermissionsPerDivision = {},
) {
  if (!domainUser?.included) return {};

  return domainUser.included
    .filter((inclusion) => inclusion.type === 'accessControl')
    .reduce((accessControls, inclusion) => {
      const divisionId = inclusion.relationships.company.data.id;
      const accessSelection = Object.fromEntries(
        permissions.map((permission) => [
          permission,
          inclusion.attributes[permission] ? 'activelySelected' : 'unselected',
        ]),
      );

      accessSelection.rolesAndCoursesEditor = rolesAndCoursesEditorSelection({
        accessSelection,
        assignablePermissions: assignablePermissionsPerDivision[divisionId],
        isRolesAndCoursesEditor:
          !!domainUser.data?.attributes?.rolesAndCoursesEditor,
      });

      accessControls[divisionId] = accessSelection;

      return accessControls;
    }, {});
}

export function rolesAndCoursesEditorSelection({
  accessSelection,
  assignablePermissions,
  isRolesAndCoursesEditor,
}) {
  if (!isRolesAndCoursesEditor) return 'unselected';

  return (
      !isAnyAssignablePermissionSelected({
        accessSelection,
        assignablePermissions,
        permissionsToCheck: permissions,
      }) ||
        isAccountPermissionInferable({
          accessSelection,
          assignablePermissions,
          permission: 'rolesAndCoursesEditor',
        })
    ) ?
      'activelySelected'
    : 'unselected';
}
